/**
 * FeatureInfo — Universal "?" Info Modal for every ProfitSpot feature
 * Explains what the feature does, why it's useful, and shows a concrete example
 */
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Info, Lightbulb, CheckCircle2, BookOpen } from 'lucide-react';

const FEATURE_CONTENT = {
  // ─── Simulator ──────────────────────────────────────────────────────────────
  simulator_overview: {
    title: 'Simulator Labs',
    icon: '⚗️',
    description: 'Simulator Labs is your DeFi "what-if" engine. It lets you model any liquidity pool investment before committing real capital — projecting true net profit after ALL real costs: gas fees, slippage, and impermanent loss.',
    why: [
      'Most DeFi dashboards only show Gross APY — the headline number that ignores fees',
      'Entry + exit costs on Ethereum can easily eat a month of yield on small positions',
      'The break-even chart shows exactly how many days until your yield covers all costs',
    ],
    example: '$1,000 at 50% APY on Arbitrum earns $1.37/day. With $2.70 in total costs, you break even in just 2 days and pocket $79.49 in 60 days.',
    tip: 'Use "Auto-Fill Market Rate" to load current pool APY with one click.',
  },
  apy_input: {
    title: 'Gross APY vs Net APY',
    icon: '📈',
    description: 'APY (Annual Percentage Yield) is the projected yearly return on your deposit. "Gross APY" is what the protocol advertises. "Net APY" is what you actually keep after all costs are subtracted.',
    why: [
      'Gross APY ignores gas fees, slippage, and impermanent loss',
      'Net APY = the true return you can take to the bank',
      'A 200% APY pool on Ethereum may have a Net APY of 170% after $16 in gas',
    ],
    example: 'Gross APY = 50%. Total costs = $17 on $1,000 over 60 days = 10.34% annualized drag. Net APY = 50% − 10.34% = 39.66%.',
    tip: 'For short positions (< 30 days), Net APY can drop dramatically. Always check the break-even day.',
  },
  gas_fees: {
    title: 'Gas Fees by Chain',
    icon: '⛽',
    description: 'Gas fees are transaction costs paid to the blockchain network every time you enter or exit a liquidity position. These are paid in the native token (ETH, MATIC, etc.) and are fixed regardless of your deposit size.',
    why: [
      'On Ethereum mainnet, a single LP transaction can cost $8–$50 depending on congestion',
      'L2 chains (Arbitrum, Base, Optimism) make small positions profitable — gas is as low as $0.05',
      'You pay gas TWICE: once to enter the pool, once to exit',
    ],
    example: 'A $500 position on Ethereum with $8 entry + $8 exit gas = $16 cost = 3.2% drag. The same position on Arbitrum = $0.20 total = 0.04% drag.',
    tip: 'ProfitSpot pre-fills gas defaults per chain. You can edit them to match current network conditions.',
  },
  tvl_slippage: {
    title: 'Pool TVL & Slippage',
    icon: '💧',
    description: 'TVL (Total Value Locked) is how much liquidity is in the pool. Slippage is the price difference between when you submit a trade and when it executes — caused by low liquidity.',
    why: [
      'High TVL pools (>$10M) have deep liquidity — slippage is very small (~0.1%)',
      'Low TVL pools (<$1M) can cause up to 0.5% slippage, eating into profits',
      'Small pool = higher APY bait, but hidden slippage cost on entry and exit',
    ],
    example: 'Depositing $1,000 into a Low TVL pool: 0.5% slippage = $5.00 cost. High TVL pool: 0.1% slippage = $1.00 cost. A $4 difference on the same deposit.',
    tip: 'Always check TVL before depositing. Aim for pools with >$2M TVL to avoid slippage surprises.',
  },
  il_calculator: {
    title: 'Impermanent Loss (IL)',
    icon: '📉',
    description: 'Impermanent Loss is the difference between holding tokens and providing them as liquidity. When token prices diverge from your entry price, you end up with fewer total dollars than if you had just held.',
    why: [
      'IL is "impermanent" because it disappears if prices return to entry levels',
      'Volatile pairs (ETH/USDT) suffer more IL than stable pairs (USDC/USDT)',
      'IL is the hidden enemy of LP farming — it can wipe out weeks of yield in a crash',
    ],
    example: 'ETH doubles in price (+100% divergence). Your LP position has ~5.7% IL. On a $1,000 position = $57 lost to IL. Your yield must exceed $57 before you profit.',
    tip: 'Stablecoin pairs (USDC/USDT, DAI/USDC) have near-zero IL. They are safest for beginners.',
  },
  breakeven_chart: {
    title: 'Break-even Chart',
    icon: '📊',
    description: 'The break-even chart visualizes exactly when your cumulative yield crosses the total cost line. The day the green line crosses the orange line = your break-even day. Every day after is pure profit.',
    why: [
      'Visualizes why short positions on high-gas chains lose money',
      'Makes the math intuitive — you can see the "dead zone" before profits begin',
      'The HODL benchmark (Pro users) shows if LP beats simply holding the tokens',
    ],
    example: 'At 50% APY with $2.70 costs: break-even at day 2. After 60 days, the green yield line is far above the orange cost line — signaling a strong position.',
    tip: 'If the cost line is above the yield line for your entire planned holding period — do not enter the position.',
  },
  net_apy_stat: {
    title: 'Net APY Stat',
    icon: '🎯',
    description: 'Net APY is the annualized return after ALL costs (gas, slippage, IL) are accounted for. It\'s the only APY number that matters for real decision-making.',
    why: [
      'Use Net APY to compare pools on equal footing',
      'A "300% APY" pool with high gas drag might net less than a "50% APY" L2 pool',
      'Net APY drops as holding period shortens (fixed costs are a bigger % of total yield)',
    ],
    example: 'Pool A: 100% Gross APY, Ethereum, 30-day hold. Net APY ≈ 79% after gas. Pool B: 60% Gross APY, Arbitrum, 30-day hold. Net APY ≈ 59%. Pool A still wins — but barely.',
    tip: 'The longer you stay in a position, the higher your Net APY (fixed costs spread over more yield days).',
  },
  // ─── Whale Tracker ──────────────────────────────────────────────────────────
  whale_tracker: {
    title: 'Whale Tracker',
    icon: '🐳',
    description: 'The Whale Tracker monitors 10 institutional-grade DeFi wallets (Jump Trading, Wintermute, Coinbase Custody, etc.) in real-time via Moralis. See where smart money is deploying capital before it moves the market.',
    why: [
      'Institutions move markets — knowing where they\'re allocating can signal emerging opportunities',
      'Whales often concentrate in pools right before yield spikes',
      'Real on-chain data (not social media speculation)',
    ],
    example: 'Jump Trading just deployed $28M into Curve and Convex. This signals confidence in stablecoin LP yields on Ethereum. Cross-reference with Yield Explorer to find similar pools.',
    tip: 'Focus on wallets labeled "Deploying" — they\'re actively entering new positions right now.',
  },
  whale_ai_insight: {
    title: 'AI Whale Intelligence',
    icon: '🤖',
    description: 'AI analyzes the whale\'s on-chain behavior, protocol allocations, and position size to infer their investment strategy and what signals it sends to retail investors.',
    why: [
      'Raw wallet data (addresses, balances) is hard to interpret without context',
      'AI translates institutional moves into actionable insights for retail investors',
      'Identifies patterns: Is the whale hedging? Farming yield? Speculating on a protocol?',
    ],
    example: 'Wintermute has 80% allocation in Uniswap V3 narrow ranges on ETH/USDC. AI insight: "Systematic market-making strategy — signals ETH price expected to remain range-bound near current levels."',
    tip: 'Read AI Whale Insights before making your own pool selection — institutional conviction is a leading indicator.',
  },
  // ─── Yield Explorer ─────────────────────────────────────────────────────────
  yield_explorer: {
    title: 'Yield Explorer + AI Intelligence',
    icon: '🔍',
    description: 'Yield Explorer aggregates live data from 6,000+ DeFi pools across 80+ chains via DeFiLlama. Filter, sort, and discover high-yield opportunities in real-time, ranked by APY, TVL, IL risk, and safety score. Now with built-in AI intelligence — click the 🧠 brain icon on any pool or tap the floating brain button for instant AI-powered analysis.',
    why: [
      'Manually checking hundreds of DeFi protocols takes hours — Explorer does it in seconds',
      'Filter by chain, protocol, stablecoin, IL risk to find pools matching YOUR risk profile',
      'Security scores (via GoPlus) flag risky contracts before you deposit',
      '🧠 AI assistant answers DeFi questions, analyzes individual pools, and compares alternatives — all without leaving the page',
    ],
    example: 'Filter: Chain=Arbitrum, IL Risk=Low, Min TVL=$5M, Sort by APY. Result: Top 10 safe Arbitrum pools with >20% APY. Then tap 🧠 on your favorite pool for AI risk analysis and "Should I invest?" advice.',
    tip: 'Use the floating 🧠 brain button (bottom-right) for general DeFi questions, or click the brain icon on any pool card for pool-specific AI analysis. Pro users get unlimited AI questions!',
  },
  pool_apy: {
    title: 'Pool APY Breakdown',
    icon: '💰',
    description: 'Each pool shows Gross APY, which is the sum of base yield (trading fees) + reward APY (incentive tokens). These components behave very differently over time.',
    why: [
      'Base APY from trading fees is sustainable — it scales with volume',
      'Reward APY from incentive tokens is temporary — protocols reduce rewards over time',
      'A pool with 90% reward APY is much riskier than one with 90% base APY',
    ],
    example: 'Pool: USDC/ETH on Uniswap V3. Base APY = 12% (trading fees). Reward APY = 8% (UNI tokens). Total = 20%. The 12% base is reliable; the 8% may drop next month.',
    tip: 'Look for pools where Base APY > Reward APY. These are more stable and sustainable.',
  },
  // ─── Portfolio Tracker ──────────────────────────────────────────────────────
  portfolio_tracker: {
    title: 'Portfolio Tracker',
    icon: '💼',
    description: 'Connect any Ethereum or L2 wallet address to see all your active LP positions, their current IL, profit/loss, and get an AI-powered "Should I Exit?" recommendation.',
    why: [
      'Tracks ALL your LP positions across chains in one view',
      'Shows real-time IL — you always know your true position value',
      'AI Exit Analysis (Pro) saves hours of manual calculation',
    ],
    example: 'Wallet: 0xabc...123. Active positions: 3. ETH/USDC on Uniswap: +$41 profit. USDC/USDT on Curve: +$12. WBTC/ETH: -$8 (underwater due to IL).',
    tip: 'Paste any wallet address — it doesn\'t have to be yours. Research top LP farmers by analyzing their portfolios.',
  },
  il_position: {
    title: 'IL Risk on Your Position',
    icon: '⚠️',
    description: 'The IL badge on your position card shows how much Impermanent Loss has accumulated since you entered the pool. "LOW" = < 3% IL. "MEDIUM" = 3-10%. "HIGH" = > 10%.',
    why: [
      'IL accumulates silently — you only notice it when you withdraw',
      'Knowing current IL helps you decide: hold (wait for prices to revert) or exit now',
      'Stablecoin positions always show NONE — they are immune to IL',
    ],
    example: 'ETH/USDC position entered at ETH = $2,000. ETH is now $2,400 (+20%). IL ≈ 0.4%. On a $5,000 position = $20 in IL. Relatively small — hold and keep earning fees.',
    tip: 'Hover over the IL badge to see the note explaining what\'s driving the IL (price divergence, stablecoin pair, etc.).',
  },
  // ─── Additional Simulator Keys ────────────────────────────────────────────
  // ─── NEW PREMIER FEATURES ───────────────────────────────────────────────────
  risk_rating: {
    title: 'Pool Risk Rating (A/B/C/D/F)',
    icon: '🏅',
    description: 'Every pool gets a composite risk letter grade combining five factors: TVL depth, APY sustainability, GoPlus security score, IL risk level, and protocol reputation. Grades run A (safest) through F (highest risk).',
    why: [
      'A single letter makes complex risk data instantly understandable',
      'Compare pools on an apples-to-apples safety basis, not just APY',
      'Catches red flags: sky-high APY on low TVL = probably grade D or F',
    ],
    example: 'USDC-ETH on Aave V3 (Ethereum): TVL $200M (25/25), APY 4% (20/20), Security 90 (23/25), IL Low (15/15), Protocol Tier 1 (15/15) = Score 98 → Grade A.',
    tip: 'Use the grade filter to show only A and B rated pools for a conservative strategy.',
  },
  security_scorecard: {
    title: 'Security Scorecard',
    icon: '🛡️',
    description: 'Powered by GoPlus Security API, every pool shows a 0-100 security score and detailed checks: honeypot detection, buy/sell tax analysis, ownership status, proxy contracts, LP lock, and holder concentration.',
    why: [
      '$137M was lost to DeFi exploits in Q1 2026 alone — security matters',
      'A "safe" looking pool can have hidden buy tax that steals your yield',
      'LP lock status tells you if the dev can rug the liquidity',
    ],
    example: 'Pool shows 🛡️ 85/100 with ✅ Honeypot Safe, ✅ No Hidden Tax, ⚠️ Top 10 holders own 42%. The score is good but watch for whale concentration.',
    tip: 'Green shield (80+) = safe. Yellow (50-79) = proceed with caution. Red (<50) = high risk.',
  },
  gas_ttp: {
    title: 'Gas-Adjusted Time to Profit',
    icon: '⛽',
    description: 'Shows exactly how many days of yield it takes to recover your entry and exit gas fees. The most practical metric for choosing between chains — answers "when do I actually break even after gas?"',
    why: [
      'A $50 gas fee on Ethereum with $1K at 10% APY takes 18 days to recover',
      'The same pool on Base costs $0.20 gas — break-even in minutes',
      'Small positions (<$2K) on mainnet may NEVER break even on gas',
    ],
    example: '$5,000 in ETH-USDC at 12.5% APY on Ethereum: Gas $50 → Break-even 29 days. On Base: Gas $0.20 → Break-even 0.1 days. The L2 comparison makes the choice obvious.',
    tip: 'Adjust the investment amount input to see how position size affects your break-even timeline.',
  },
  chain_leaderboard: {
    title: 'Trending Chains Leaderboard',
    icon: '🔥',
    description: 'Real-time ranking of the top-performing blockchain networks by yield. See which chains have the most pools, highest APYs, and largest TVL — and spot emerging trends before they go mainstream.',
    why: [
      'Yield opportunities shift between chains as incentive programs launch and end',
      'L2 chains often have higher yields due to protocol growth incentives',
      'A chain marked HOT has many pools above 20% APY — signal of active liquidity mining',
    ],
    example: 'Base: 9.1K% top APY, 2,072 pools, $3.3B TVL, tagged HOT + L2. Click to filter the pool list to only Base pools.',
    tip: 'Click any chain card to instantly filter the Yield Explorer to that chain\'s pools.',
  },
  btc_defi: {
    title: 'Bitcoin DeFi Spotlight',
    icon: '₿',
    description: 'Dedicated section tracking yield opportunities for BTC-related tokens: WBTC, cbBTC, tBTC, LBTC, and more. Bitcoin DeFi TVL surged $1.7B in a single week — this section helps you capture that wave.',
    why: [
      'BTC holders can now earn yield without selling their Bitcoin',
      'Wrapped BTC tokens (cbBTC, WBTC) unlock Ethereum/L2 DeFi yields',
      'BTC restaking (Babylon Protocol) is the fastest-growing DeFi category',
    ],
    example: 'WBTC-ETH on Uniswap V3 (Ethereum): 6.4K% APY, $240K TVL, Grade D. High yield but elevated risk — the grade tells the story.',
    tip: 'Look for BTC pools with Grade B or higher for safer yield on your Bitcoin holdings.',
  },
  portfolio_risk_analysis: {
    title: 'Rate My Portfolio',
    icon: '📊',
    description: 'Scans your wallet\'s LP positions and assigns each a risk grade. Then calculates a weighted portfolio-level grade and finds yield optimization opportunities — showing you exactly how much more you could earn in safer pools.',
    why: [
      'Most LP farmers don\'t know their portfolio\'s true risk profile',
      'Better alternatives may exist — the optimizer finds pools with higher APY AND better grades',
      'The total optimization potential number is your "money left on the table" figure',
    ],
    example: 'Portfolio Grade: B (72/100). Position 1: USDC-ETH on Uniswap at 4.2% → Switch to Aerodrome (Base) at 14.1% for +$1,980/yr more on your $20K position.',
    tip: 'The optimizer looks for pools that are at least 20% better in APY while maintaining similar or better risk grades.',
  },
  pool_alerts: {
    title: 'Pool APY/TVL Alerts',
    icon: '🔔',
    description: 'Set threshold-based alerts on any pool. Get notified when APY drops below your target, rises above a level, or TVL drops significantly — so you can rebalance before it\'s too late.',
    why: [
      'APY can drop 50% overnight when incentive programs end',
      'TVL drops often precede rug pulls or protocol issues',
      'Passive monitoring lets you manage positions without constant checking',
    ],
    example: 'Set alert: "Notify me if USDC-ETH APY drops below 8%." Three days later, APY drops to 6.2% → you get an email and can exit or rebalance.',
    tip: 'Pro users get 3 alerts. Elite users get unlimited alerts with Telegram notifications.',
  },
  // ─── Additional Simulator Keys ────────────────────────────────────────────
  deposit_amount: {
    title: 'Deposit Amount',
    icon: '💵',
    description: 'The amount of money (in USD) you plan to deposit into the liquidity pool. This is the principal that earns yield and is subject to IL and gas costs.',
    why: [
      'Larger deposits dilute fixed costs (gas, slippage) → higher Net APY',
      'A $10,000 deposit pays the same $8 gas as a $100 deposit — but it\'s only 0.08% drag vs 8%',
      'Adjust this to match your actual planned investment size for accurate projections',
    ],
    example: '$500 on Ethereum with $16 gas: 3.2% drag. $10,000 on Ethereum with $16 gas: 0.16% drag. Same gas, very different impact.',
    tip: 'If your deposit is small (< $500) on Ethereum, consider L2 chains like Arbitrum or Base where gas is < $0.20.',
  },
  holding_period: {
    title: 'Holding Period',
    icon: '📅',
    description: 'How many days you plan to hold your LP position. Longer holding periods spread your fixed costs (gas, slippage) over more yield days, dramatically improving Net APY.',
    why: [
      'Short holds (< 7 days) are rarely profitable on Ethereum mainnet due to gas',
      'The longer you hold, the more your yield compounds above fixed costs',
      'Break-even days = Total Costs ÷ Daily Yield — you need to hold at least this long',
    ],
    example: 'At 50% APY on $1,000: break-even at ~2 days on Arbitrum. After 30 days: $41 profit. After 90 days: $123 profit. Each extra day adds $1.37.',
    tip: 'For positions under $1,000 on mainnet, aim for 60+ day holds to make gas costs worthwhile.',
  },
  stablecoin_pool: {
    title: 'Stablecoin Pool (No IL)',
    icon: '🪙',
    description: 'Toggle this ON when you\'re simulating a pool where both tokens are stablecoins (e.g., USDC/USDT, DAI/USDC). These pools are immune to Impermanent Loss because neither token changes price relative to the other.',
    why: [
      'Stablecoin pairs have near-zero IL — your principal is always safe',
      'APY is lower (5-20%) but risk-adjusted returns are often better than volatile pairs',
      'Perfect for conservative DeFi yield farming with predictable returns',
    ],
    example: 'USDC/USDT pool: $1,000 deposit, 8% APY, 30 days. No IL. Net Profit = (8%/365×30×$1000) - $0.10 gas (Base) = $6.47. Reliable, low-drama yield.',
    tip: 'Enabling this toggle zeroes out the IL calculation, giving you a pure yield-vs-costs picture.',
  },
  price_divergence: {
    title: 'Price Divergence & Impermanent Loss',
    icon: '📉',
    description: 'Price divergence is how much one token\'s price has moved away from its price when you entered the pool. Higher divergence = more Impermanent Loss. The IL formula is: IL = (2√d/(1+d)) − 1 where d = price ratio.',
    why: [
      'Even if you profit on fees, IL can put you underwater if prices move sharply',
      'A 50% price move causes ~5.7% IL — on $5,000 that\'s $285 of hidden loss',
      'ETH/altcoin pairs suffer more IL than ETH/USDC because alts move wilder',
    ],
    example: 'Enter at ETH = $2,000. ETH goes to $3,000 (+50%). IL = 2.0% ≈ $20 on $1,000 position. Your yield must exceed $20 to show real profit.',
    tip: 'Set price divergence to your realistic estimate of where prices might go. Use historical volatility as a guide.',
  },
  breakeven_chart_section: {
    title: 'Break-even Chart',
    icon: '📊',
    description: 'This chart plots your cumulative yield (green line) growing every day vs your fixed total costs (orange horizontal line). The day the green crosses the orange = your break-even point. Every day after is profit.',
    why: [
      'Instantly visualizes whether your position is profitable for your planned hold',
      'If the cost line is above the yield line for your entire period: do NOT enter',
      'Pro users see the HODL benchmark: is LP farming better than just holding the tokens?',
    ],
    example: 'Scenario: 50% APY, $1,000, Arbitrum. Orange cost line at $2.70. Green yield reaches $2.70 after 2 days. At 60 days, green = $82.19. Net profit = $79.49.',
    tip: 'A steeper green line means higher APY — the faster it rises above costs, the better the deal.',
  },
  projected_returns: {
    title: 'Projected Returns',
    icon: '💰',
    description: 'Shows your total Gross Yield and Net Profit for the selected holding period. Gross Yield = raw APY earnings. Net Profit = Gross Yield minus ALL costs (gas + slippage + IL).',
    why: [
      'The number DeFi protocols advertise (APY) is never what you take home',
      'Net Profit is the only honest number — it\'s what actually lands in your wallet',
      'Daily yield rate helps you understand how the position performs day-by-day',
    ],
    example: '$1,000 × 50% APY × 60 days = $82.19 Gross. Minus $2.70 costs = $79.49 Net. Daily rate: $1.37/day.',
    tip: 'Compare Net Profit across different chains using the chain selector — L2 nets are almost always better for small deposits.',
  },
  // ─── Smart Exit Timer ────────────────────────────────────────────────────────
  smart_exit_timer: {
    title: 'Smart Exit Timer',
    icon: '⏱️',
    description: 'The Smart Exit Timer analyzes your live LP position using current token prices (via CoinGecko) and tells you whether to EXIT NOW, HOLD FOR GAINS, or WAIT TO BREAK EVEN. It compares your current profit against what you could earn elsewhere.',
    why: [
      'DeFi LPs often exit too early (leaving yield on the table) or too late (IL wipes gains)',
      'The timer factors in: days held, current prices, daily yield rate, and opportunity cost',
      '"Opportunity cost" = what the best alternative pool on your chain is currently paying',
    ],
    example: '$1,000 position, entered 20 days ago, earned $27. Best alternative on Arbitrum: $55 in 20 days. Verdict: "Consider Exiting — you\'re underperforming by $28. Reallocate and earn more."',
    tip: 'Run this whenever you\'re unsure whether to stay or go. The math doesn\'t lie.',
  },
  // ─── Advanced Backtesting ────────────────────────────────────────────────────
  advanced_backtesting: {
    title: 'Advanced Backtesting',
    icon: '📡',
    description: 'Advanced Backtesting uses historical on-chain data (via Bitquery) to simulate how your LP position would have actually performed over a past time period — using real historical prices, not estimates.',
    why: [
      'Forward simulation uses assumptions; backtesting uses REAL historical data',
      'See how a pool performed during specific market events (e.g., the May 2022 crash)',
      'Build conviction before entering: "This ETH/USDC pool returned 18% in the last 90 days"',
    ],
    example: 'Backtest ETH/USDC on Uniswap V3, $10,000 deposit, last 90 days. Result: Gross Yield $1,850 (74% annualized), IL: $320, Net: $1,530 (61% annualized). Beat ETH HODL by 12%.',
    tip: 'Use backtesting to validate a pool before committing capital. Look for consistent performance, not just peak periods.',
  },
  // ─── Alerts ──────────────────────────────────────────────────────────────────
  alerts: {
    title: 'Smart Yield Alerts',
    icon: '🔔',
    description: 'Smart Yield Alerts monitor DeFi pools 24/7 and notify you instantly when important events happen: a pool\'s APY spikes or drops, a whale enters a position, or your LP position reaches its break-even point.',
    why: [
      'DeFi moves fast — APYs can double or halve within hours during protocol events',
      'Email alerts let you act on opportunities without watching dashboards all day',
      'Telegram alerts deliver notifications straight to your phone in real-time',
    ],
    example: 'Alert set: "Notify me if USDC/ETH APY on Arbitrum > 40%." Protocol launches liquidity mining. APY hits 78%. You receive an email at 2:47 AM. You deposit at 3:05 AM before the crowd.',
    tip: 'Set APY threshold alerts for your top 3 watchlist pools. Combine with Whale Tracker — if a whale enters a pool AND APY spikes, that\'s a high-conviction signal.',
  },
  // ─── Scenario Presets ───────────────────────────────────────────────────────
  scenario_presets: {
    title: 'Simulation Presets',
    icon: '🎮',
    description: 'Presets are one-click scenario templates that load all simulator inputs at once. Use them to quickly explore different DeFi strategies without manually adjusting each setting.',
    why: [
      'Perfect for beginners learning what different strategies look like in practice',
      'Compare strategies side-by-side by switching presets and noting Net APY',
      'Each preset reflects a real and common DeFi investing approach',
    ],
    example: 'Select "Stable Pair" to simulate a USDC/USDT position on Base: 8% APY, zero IL, minimal gas. Then switch to "Degen" to see what 200% APY looks like after IL and low-TVL slippage.',
    tip: 'Start with a preset, then customize individual inputs to match a specific pool you\'re researching.',
  },
};

export const FeatureInfo = ({ featureKey, className = '', size = 'sm' }) => {
  const [open, setOpen] = useState(false);
  const content = FEATURE_CONTENT[featureKey];
  if (!content) return null;

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`inline-flex items-center justify-center rounded-full font-bold transition-all flex-shrink-0 
          bg-[#00d4ff]/15 text-[#00d4ff] border border-[#00d4ff]/25
          hover:bg-[#00d4ff]/30 hover:border-[#00d4ff]/50 hover:scale-110
          ${size === 'xs' ? 'w-5 h-5 text-[11px]' : 'w-5 h-5 text-[11px]'}
          ${className}`}
        title={`Learn about ${content.title}`}
        data-testid={`info-btn-${featureKey}`}
      >
        ?
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          role="presentation"
        >
          <div
            className="bg-[#0d0d1a] border border-gray-700/60 rounded-2xl max-w-lg w-full shadow-2xl flex flex-col max-h-[85vh] overflow-visible"
            onClick={(e) => e.stopPropagation()}
            data-testid={`info-modal-${featureKey}`}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800 flex-shrink-0">
              <div className="flex items-center gap-3">
                <span className="text-2xl leading-none">{content.icon}</span>
                <h3 className="text-base font-bold text-white">{content.title}</h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 text-gray-500 hover:text-white transition-colors rounded-lg hover:bg-gray-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
              {/* Description */}
              <div className="flex gap-3">
                <BookOpen className="w-4 h-4 text-[#00d4ff] mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-300 leading-relaxed">{content.description}</p>
              </div>

              {/* Why it matters */}
              <div className="bg-gray-900/50 rounded-xl p-3.5 border border-gray-800/50">
                <div className="flex items-center gap-2 mb-2.5">
                  <Lightbulb className="w-3.5 h-3.5 text-[#ffa502]" />
                  <p className="text-xs font-bold text-[#ffa502] uppercase tracking-wide">Why it matters</p>
                </div>
                <ul className="space-y-1.5">
                  {content.why.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="w-3 h-3 text-gray-600 mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-gray-400 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Example */}
              <div className="bg-[#00ff88]/5 rounded-xl p-3.5 border border-[#00ff88]/15">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="w-3.5 h-3.5 text-[#00ff88]" />
                  <p className="text-xs font-bold text-[#00ff88] uppercase tracking-wide">Example</p>
                </div>
                <p className="text-xs text-gray-300 leading-relaxed">{content.example}</p>
              </div>

              {/* Tip */}
              {content.tip && (
                <div className="flex items-start gap-2 px-1">
                  <span className="text-[#00d4ff] text-xs font-bold flex-shrink-0">PRO TIP</span>
                  <p className="text-xs text-gray-500 leading-relaxed">{content.tip}</p>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default FeatureInfo;
